/**
 * Credits on the user detail page: what the account has left of everything it
 * was granted, the ledger of grants behind that, and (for platform admins) a
 * way to gift more.
 *
 * Gifted credits join the same balance as purchases, referral rewards and deal
 * credits. They are spent after the plan's monthly allowance and do not reset
 * with it, so a gift is never wasted by the month rolling over.
 */

import { useState } from "react";
import { Gift } from "@phosphor-icons/react";
import { Alert, type PillTone, StatusPill } from "@charisol-spark/ui";
import { hasRole } from "@/components/auth/AdminGuard";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Spinner } from "@/components/ui/Spinner";
import { useAdminUserCredits, useGiftCredits } from "@/hooks/useAdmin";
import type { CreditLedgerEntry, CreditLedgerKind } from "@/services/admin";
import { getErrorMessage } from "@/services/api";

const SECTION_CLASS =
  "space-y-3 rounded-xl border border-border/50 bg-card p-5";

export const MAX_GIFT_CREDITS = 100_000;

const KIND_LABEL: Record<CreditLedgerKind, string> = {
  gift: "Gift",
  referral: "Referral",
  deal: "Deal",
  purchase: "Purchase",
  comp: "Comp",
};

const KIND_TONE: Record<CreditLedgerKind, PillTone> = {
  gift: "violet",
  referral: "blue",
  deal: "amber",
  purchase: "green",
  comp: "neutral",
};

const KIND_HINT: Record<CreditLedgerKind, string> = {
  gift: "Given by an admin",
  referral: "A referral reward",
  deal: "Distributed by Spark deal credits",
  purchase: "A credit pack the user bought",
  comp: "Offsets this period's usage; it does not raise the balance",
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** A fresh key per submission, so a retry of the same one never grants twice. */
function newIdempotencyKey(): string {
  return `gift-${crypto.randomUUID()}`;
}

export function GiftCreditsDialog({
  userId,
  userLabel,
  open,
  onOpenChange,
}: {
  userId: string;
  userLabel: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const gift = useGiftCredits();
  const [credits, setCredits] = useState("");
  const [reason, setReason] = useState("");
  // Held across retries of one submission; replaced once it has gone through.
  const [key, setKey] = useState(newIdempotencyKey);
  const [done, setDone] = useState<string | null>(null);

  const amount = Number(credits);
  const amountValid =
    Number.isInteger(amount) && amount >= 1 && amount <= MAX_GIFT_CREDITS;
  const reasonValid = reason.trim().length >= 3;

  function close(next: boolean) {
    if (!next) {
      setCredits("");
      setReason("");
      setDone(null);
      setKey(newIdempotencyKey());
      gift.reset();
    }
    onOpenChange(next);
  }

  function submit() {
    if (!amountValid || !reasonValid) return;
    gift.mutate(
      {
        userId,
        body: { credits: amount, reason: reason.trim(), idempotencyKey: key },
      },
      {
        onSuccess: (result) => {
          setDone(
            result.duplicate
              ? "This gift had already been made, so nothing more was added."
              : `Gifted ${result.credits.toLocaleString()} credits. The balance is now ${result.boughtCredits.toLocaleString()}.`
          );
          setCredits("");
          setReason("");
          setKey(newIdempotencyKey());
        },
      }
    );
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Gift credits</DialogTitle>
          <DialogDescription>
            Add credits to {userLabel}. They are spent after the plan&apos;s
            monthly allowance, do not reset each month and do not expire. The
            gift is recorded in the audit log with your reason.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <Alert variant="success">{done}</Alert>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="gift-credits">Credits</Label>
              <Input
                id="gift-credits"
                type="number"
                inputMode="numeric"
                min={1}
                max={MAX_GIFT_CREDITS}
                step={1}
                value={credits}
                onChange={(e) => setCredits(e.target.value)}
                placeholder="e.g. 200"
              />
              <p className="text-xs text-muted-foreground">
                A whole number from 1 to {MAX_GIFT_CREDITS.toLocaleString()}.
                About 10 credits make one post.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="gift-reason">Reason</Label>
              <Input
                id="gift-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                placeholder="Why this account is getting credits"
              />
            </div>

            {gift.isError && (
              <Alert variant="error">
                {getErrorMessage(gift.error, "Failed to gift credits.")}
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => close(false)}>
            {done ? "Close" : "Cancel"}
          </Button>
          {!done && (
            <Button
              onClick={submit}
              disabled={!amountValid || !reasonValid || gift.isPending}
            >
              {gift.isPending ? "Gifting..." : "Gift credits"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LedgerRow({ entry }: { entry: CreditLedgerEntry }) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
      <div className="flex min-w-0 items-center gap-2">
        <span title={KIND_HINT[entry.kind]}>
          <StatusPill tone={KIND_TONE[entry.kind]}>
            {KIND_LABEL[entry.kind]}
          </StatusPill>
        </span>
        <span className="min-w-0 truncate text-muted-foreground">
          {entry.note ??
            (entry.grantedBy ? `By ${entry.grantedBy}` : KIND_HINT[entry.kind])}
        </span>
      </div>
      <div className="flex items-center gap-3 tabular-nums">
        <span className="text-xs text-muted-foreground">
          {formatDate(entry.grantedAt)}
        </span>
        <span
          className={
            entry.raisesBalance ? "font-medium" : "text-muted-foreground"
          }
        >
          {entry.raisesBalance ? "+" : ""}
          {entry.credits.toLocaleString()}
        </span>
      </div>
    </li>
  );
}

export function UserCreditsPanel({
  userId,
  userLabel,
}: {
  userId: string;
  userLabel: string;
}) {
  const { data, isLoading, error } = useAdminUserCredits(userId);
  const [giftOpen, setGiftOpen] = useState(false);
  const canGift = hasRole("platform_admin");

  return (
    <div className={SECTION_CLASS}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Credits</h2>
          <p className="text-xs text-muted-foreground">
            Bought and gifted credits, spent after the monthly allowance.
          </p>
        </div>
        {canGift && (
          <Button size="sm" variant="outline" onClick={() => setGiftOpen(true)}>
            <Gift className="mr-1.5 h-3.5 w-3.5" />
            Gift credits
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex h-16 items-center justify-center">
          <Spinner size="sm" />
        </div>
      ) : error || !data ? (
        <Alert variant="error">
          {getErrorMessage(error, "Failed to load this account's credits.")}
        </Alert>
      ) : (
        <>
          <p className="text-2xl font-semibold tabular-nums">
            {data.boughtCredits.toLocaleString()}
            <span className="ml-1.5 text-sm font-normal text-muted-foreground">
              credits left
            </span>
          </p>
          {data.grants.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No grants yet. Purchases, referral rewards, deal credits and gifts
              will be listed here.
            </p>
          ) : (
            <ul className="divide-y divide-border/50">
              {data.grants.map((entry) => (
                <LedgerRow key={entry.ref} entry={entry} />
              ))}
            </ul>
          )}
        </>
      )}

      {canGift && (
        <GiftCreditsDialog
          userId={userId}
          userLabel={userLabel}
          open={giftOpen}
          onOpenChange={setGiftOpen}
        />
      )}
    </div>
  );
}
